<template>
    <BaseModal
        class="bill-applicable-discounts-modal"
        :closable="true"
        :hasHeader="false"
        :hasFooter="false"
        size="modal-lg"
        ref="baseModalComponent"
    >
        <template v-slot:body>
            <ApplicableDiscountsList
                v-if="isVisible"
                :inOperatorApp="true"
                :selectedMainBillItemIds="applyToCart ? undefined : bill.selectedItemsList.map(c => c.billItemId)"
                :locationId="allocation.locationId!"
                :cartId="applyToCart ? cart.currentCart?.cartId : undefined"
                :selectedMainCartItemIds="applyToCart ? cart.selectedMainCartItemIds : undefined"
                :billId="applyToCart ? undefined : bill.currentBill!.id"
                :payUseCaseId="applyToCart ? undefined : paymentStore.paymentService?.payUseCase?.id!"
                @billDiscountApplied="onBillDiscountApplied"
                @cartDiscountApplied="onCartDiscountApplied"
                @applyDiscountErrorOccurred="onApplyDiscountError"
            />
        </template>
    </BaseModal>
</template>

<script setup lang="ts">
    import type { DaoError } from "@/data/dao/service/BaseServiceDao";
    import type { ICartDiscountAppliedEventData } from "@/data/model/Voucher";
    import { onApplyDiscountErrorOccurred } from "@/data/services/DiscountsService";
    import { useBillStore } from "@/data/stores/bill";
    import { useCartStore } from "@/data/stores/cart";
    import { usePaymentStore } from "@/data/stores/payment";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { Logging } from "@/data/util/Logging";
    import { translations } from "@/shared/localization/translations";
    import { computed, ref, watch } from "vue";

    import ApplicableDiscountsList from "./ApplicableDiscountsList.vue";
    import BaseModal from "@/components/dialog/BaseModal.vue";

    const allocation = useTableAllocationStore();
    const bill = useBillStore();
    const cart = useCartStore();
    const paymentStore = usePaymentStore();

    // define properties
    withDefaults(
        defineProps<{
            applyToCart?: boolean;
        }>(),
        {
            applyToCart: false,
        }
    );

    // define html refs
    const baseModalComponent = ref<InstanceType<typeof BaseModal> | null>(null);
    const originalAllocationHash = ref<string | undefined>();
    const isVisible = computed(() => baseModalComponent.value?.isVisible);

    function onBillDiscountApplied() {
        close();
    }

    function onCartDiscountApplied(eventData: ICartDiscountAppliedEventData) {
        cart.setCurrentCart(eventData.cart);
        close();
    }

    function onApplyDiscountError(error: DaoError) {
        onApplyDiscountErrorOccurred(
            error,
            { containerClass: "bill-applicable-discounts-error-popup" },
            $("body"),
            translations.pay.failedNoItemsSelected
        );
    }

    watch(
        () => allocation.hash,
        () => {
            if (baseModalComponent?.value?.isVisible != true) return;

            if (allocation.hash != originalAllocationHash.value) {
                // if allocation has changed - close this modal
                Logging.LogInfo(
                    `Closing BillApplicableDiscountsModal because allocation has changed - previous ${originalAllocationHash.value}, current ${allocation.hash}`
                );
                close();
            }
        }
    );

    // define exposed
    function show() {
        originalAllocationHash.value = allocation.hash;
        baseModalComponent.value?.show();
    }

    function close() {
        baseModalComponent.value?.close();
    }

    defineExpose({
        show,
        close,
    });
</script>

<style lang="scss">
    .modal.bill-applicable-discounts-modal {
        .modal-body {
            padding: 1.5em 2em;
        }
    }

    .bill-applicable-discounts-error-popup {
        z-index: 1510;
    }
</style>
